import { router } from 'expo-router'
import { memo, useEffect, useState } from 'react'
import { Platform, Pressable, Text, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { StyleSheet } from 'react-native-unistyles'

import FosterThumbnail from '@/components/fosters/FosterThumbnail'
import { ContextMenuWrapper } from '@/components/shared/ContextMenuWrapper'
import UniIcon from '@/components/unistyles/UniIcon'
import UniLinearGradient from '@/components/unistyles/UniLinearGradient'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import Species from '@/enums/species'
import { useFosterActions } from '@/hooks/useFosterActions'
import { useAppStore } from '@/store/useAppStore'
import { addAction, RumActionType } from '@/utils/datadog/datadog'
import { triggerHapticClick } from '@/utils/haptics'
import buildOrgPath from '@/utils/orgRoute'

interface Props {
  foster: {
    id: string
    blurhash: string | null
    deceasedDate: Date | null
    thumbUri: string | null
    species: 'Dog' | 'Cat' | null
    name: string
    user: {
      firstName: string
      lastName: string
    }
    adoptedName?: string | null
  }
  isSelected: boolean
  onSelect(id: string): void
}

const FosterGridItem = ({ foster, isSelected, onSelect }: Props) => {
  const [isHovered, setIsHovered] = useState(false)

  // Animated values for hover dot
  const hoverDotOpacity = useSharedValue(0)

  const bulkSelect = useAppStore.use.bulkSelect()
  const { actions } = useFosterActions(foster.id)

  const advancedFilters = useAppStore.use.advancedFilters()
  const basicFilters = useAppStore.use.basicFilters()

  let fosterName = foster.name
  if (foster.adoptedName) {
    fosterName = `${foster.name} (${foster.adoptedName})`
  }

  // Animate hover dot opacity based on hover state
  useEffect(() => {
    if (!isSelected && isHovered) {
      hoverDotOpacity.value = withTiming(1, { duration: 200 })
    } else {
      hoverDotOpacity.value = withTiming(0, { duration: 150 })
    }
  }, [isHovered, isSelected, hoverDotOpacity])

  // Get specific actions from useFosterActions
  const deleteAction = actions.find((action) => action.id === 'delete')
  const copyAction = actions.find((action) => action.id === 'copy-foster')
  const scanAction = actions.find((action) => action.id === 'scan-documents')
  const printCoverSheetAction = actions.find(
    (action) => action.id === 'print-cover-sheet'
  )
  const printAdoptionContractAction = actions.find(
    (action) => action.id === 'print-adoption-contract'
  )

  const handleSelectFoster = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Select - Foster (Context Menu)`,
    })
    onSelect(foster.id)
    triggerHapticClick()
  }

  const handleHoverSelectFoster = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Select - Foster (Hover)`,
    })
    onSelect(foster.id)
    triggerHapticClick()
  }

  const handleScanDocuments = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Scan Documents (Context Menu)`,
    })
    scanAction?.onPress()
  }

  const handleCopyFoster = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Copy Foster (Context Menu)`,
    })
    copyAction?.onPress()
  }

  const handleDeleteFoster = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Delete - Foster (Context Menu)`,
    })
    deleteAction?.onPress()
  }

  const handlePrintCoverSheet = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Print Cover Sheet (Context Menu)`,
    })
    printCoverSheetAction?.onPress()
  }

  const handlePrintAdoptionContract = () => {
    addAction({
      type: RumActionType.TAP,
      name: `Print Adoption Contract (Context Menu)`,
    })
    printAdoptionContractAction?.onPress()
  }

  const handleContextMenuPress = (index: number) => {
    // Calculate the handlers array based on available actions
    const handlers = [
      handleSelectFoster, // 0
      ...(scanAction ? [handleScanDocuments] : []), // 1 if available
      ...(copyAction ? [handleCopyFoster] : []), // 2 next if available
      // Print actions come next if they exist
      ...(printCoverSheetAction ? [handlePrintCoverSheet] : []),
      ...(printAdoptionContractAction ? [handlePrintAdoptionContract] : []),
      // Delete action comes last if it exists
      ...(deleteAction ? [handleDeleteFoster] : []),
    ]

    if (handlers[index]) {
      handlers[index]()
    }
  }

  // Build context menu actions
  const printActions = []
  if (printCoverSheetAction) {
    printActions.push({
      title: 'Print Cover Sheet',
      icon: 'newspaper-variant',
    })
  }
  if (printAdoptionContractAction) {
    printActions.push({
      title: 'Print Adoption Contract',
      icon: 'clipboard-flow',
    })
  }

  const contextMenuActions = [
    {
      title: isSelected ? 'Deselect Foster' : 'Select Foster',
      icon: isSelected ? 'check' : 'plus',
    },
    ...(scanAction
      ? [
          {
            title: 'Scan Documents',
            icon: scanAction.icon,
          },
        ]
      : []),
    ...(copyAction
      ? [
          {
            title: 'Copy Foster',
            icon: copyAction.icon,
          },
        ]
      : []),
    ...(printActions.length > 0
      ? [
          {
            title: 'Print',
            icon: 'printer',
            actions: printActions,
          },
        ]
      : []),
    ...(deleteAction
      ? [
          {
            title: 'Delete Foster',
            icon: deleteAction.icon,
            destructive: true,
          },
        ]
      : []),
  ]

  // Animated style for hover dot
  const animatedHoverDotStyle = useAnimatedStyle(() => {
    return {
      opacity: hoverDotOpacity.value,
    }
  })

  return (
    <ContextMenuWrapper
      actions={contextMenuActions}
      onPress={handleContextMenuPress}
      style={styles.contextMenuWrapper}
      title={foster.name}
      wrapperKey={foster.id}
    >
      <UniTouchableRipple
        accessibilityRole="button"
        borderless
        {...Platform.select({
          web: {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          },
          // This doesn't work out of the box without enabling a react native feature flag https://reactnative.dev/blog/2022/12/13/pointer-events-in-react-native#enable-feature-flags
          ios: {
            onHoverIn: () => setIsHovered(true),
            onHoverOut: () => setIsHovered(false),
          },
        })}
        // We register a noop onLongPress to prevent a false click from occurring when the parent ContextMenu is handling it's onLongPress
        onLongPress={() => {}}
        onPress={() => {
          if (isSelected) {
            addAction({
              type: RumActionType.TAP,
              name: `Deselect - Foster`,
            })
            onSelect(foster.id)
            triggerHapticClick()
            return
          }

          if (bulkSelect.selection.items.length > 0) {
            addAction({
              type: RumActionType.TAP,
              name: `Select - Foster`,
            })
            onSelect(foster.id)
            triggerHapticClick()
          } else {
            addAction({
              type: RumActionType.TAP,
              name: `View - Foster`,
            })
            router.navigate({
              pathname: buildOrgPath('/fosters/[id]'),
              params: { id: foster.id, from: 'fosters' },
            })
          }
        }}
        style={styles.touchableContainer}
        testID={`foster-link-${foster.id}`}
      >
        <View style={[styles.imageContainer]}>
          {isSelected && <View style={styles.selectedBorder} />}

          {isSelected && (
            <View style={styles.selectedCheck}>
              <UniIcon color="white" size={20} source="check" />
            </View>
          )}

          {/* Animated hover dot - always rendered but with animated opacity */}
          <Animated.View style={[styles.hoverDot, animatedHoverDotStyle]}>
            <Pressable
              accessibilityRole="button"
              onPress={handleHoverSelectFoster}
              style={styles.hoverDotPressable}
              testID={`hover-select-${foster.id}`}
            >
              <UniIcon color="white" size={16} source="check" />
            </Pressable>
          </Animated.View>

          <FosterThumbnail
            blurhash={foster.blurhash}
            species={foster.species as Species}
            style={StyleSheet.absoluteFill}
            thumbUri={foster.thumbUri}
            transition={200}
          />
          {foster.deceasedDate && <View style={styles.blackAndWhiteOverlay} />}
          <UniLinearGradient
            colors={[
              'transparent',
              'rgba(0,0,0,0.5)',
              'rgba(0,0,0,0.7)',
              'rgba(0,0,0,0.9)',
            ]}
            style={[
              styles.gradientContainer,
              (basicFilters.filters.viewAll || advancedFilters.isActive) &&
                styles.gradientContainerExpanded,
            ]}
          />
          <View style={styles.cardLabelContainer}>
            <Text style={styles.fosterName} testID={`foster-name-${foster.id}`}>
              {fosterName}
            </Text>

            {(basicFilters.filters.viewAll || advancedFilters.isActive) && (
              <Text
                style={styles.volunteerName}
                testID={`user-name-${foster.id}`}
              >
                {foster.user.firstName} {foster.user.lastName}
              </Text>
            )}
          </View>
        </View>
      </UniTouchableRipple>
    </ContextMenuWrapper>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    blackAndWhiteOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1,
    },
    cardLabelContainer: {
      bottom: 0,
      padding: theme.tokens.spacing[3],
      position: 'absolute',
      width: '100%',
    },
    contextMenuWrapper: {
      aspectRatio: 1,
      flex: 1,
    },
    gradientContainer: {
      bottom: 0,
      height: '35%',
      position: 'absolute',
      width: '100%',
    },
    gradientContainerExpanded: {
      height: '45%',
    },
    hoverDot: {
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      borderRadius: 100,
      height: 28,
      justifyContent: 'center',
      margin: 6,
      position: 'absolute',
      width: 28,
      zIndex: 3,
    },
    hoverDotPressable: {
      alignItems: 'center',
      borderRadius: 100,
      height: 28,
      justifyContent: 'center',
      width: 28,
    },
    imageContainer: {
      borderColor: theme.colors.outlineVariant,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 0.5,
      height: '100%',
      overflow: 'hidden',
      width: '100%',
    },
    selectedBorder: {
      backgroundColor: 'transparent',
      borderColor: theme.colors.primary,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: theme.tokens.spacing[2],
      bottom: 0,
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 2,
    },
    selectedCheck: {
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 100,
      height: 30,
      justifyContent: 'center',
      margin: 6,
      position: 'absolute',
      width: 30,
      zIndex: 2,
    },
    fosterName: {
      color: 'white',
      fontSize: 22,
      fontWeight: '400',
      lineHeight: 28,
    },
    volunteerName: {
      color: 'white',
      fontSize: 14,
      fontWeight: '400',
      lineHeight: 20,
    },
    touchableContainer: {
      borderRadius: theme.tokens.containerBorderRadius,
      flex: 1,
      overflow: 'hidden',
      width: '100%',
    },
  }
})

export default memo(FosterGridItem)
